import { PAGE_API_QUERY_KEY_HEAD } from '@Core/clients/pageApiQueryKeys';
import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  ApiError,
  SdkSendNotificationHandle,
  SdkSendNotificationInput,
  SdkSendNotificationResponse,
  SdkUseSendNotificationOptions
} from '@Pages/pageRuntime/sdk/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { NOTIFICATION_HISTORY_QUERY_KEY } from './useNotificationHistory';

export const SEND_NOTIFICATION_URL = '/api/email-messages/send-notification';

export function useSendNotification(
  options?: SdkUseSendNotificationOptions
): SdkSendNotificationHandle {
  const queryClient = useQueryClient();
  const mutation = useMutation<SdkSendNotificationResponse, ApiError, SdkSendNotificationInput>({
    mutationFn: (input) =>
      axios.post<SdkSendNotificationResponse>(SEND_NOTIFICATION_URL, input).then(
        (response) => response.data,
        (error: unknown) => {
          throw toApiError(error);
        }
      ),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['email-messages', 'search'] });
      void queryClient.invalidateQueries({
        queryKey: [PAGE_API_QUERY_KEY_HEAD, NOTIFICATION_HISTORY_QUERY_KEY]
      });

      options?.onSuccess?.(response);
    },
    onError: (error) => options?.onError?.(error)
  });

  return {
    send: (input) =>
      mutation.mutateAsync(input).then(
        (response) => response ?? null,
        () => null
      ),
    data: mutation.data,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: () => {
      mutation.reset();
    }
  };
}
